 ((exports) => {
	const sortCreditsHighToLow = "credits-high-to-low";
	const sortCreditsLowToHigh = "credits-low-to-high";

	const creditDifference = (a, b) =>
		(a.maxPotentialCredits - b.maxPotentialCredits)
		|| (a.minPotentialCredits - b.minPotentialCredits);

	const PathwaySorting = {
		[sortCreditsHighToLow]: (a, b) => creditDifference(b, a),

		[sortCreditsLowToHigh]: (a, b) => creditDifference(a, b)
	};

	/**
	 * @type PreSelectedProgram
	 * @field {int} institutionProgramId
	 * @field {string} titleDisplay
	 */

	/**
	 * @type DetailedSelectedProgram extends ProgramName
	 * @field {int} institutionProgramId
	 * @field {bool} isResolved
	 */

	/**
	 * @callback GetProgramNameUrl
	 * @param {int} institutionProgramId
	 * @returns {string} url
	 */

	/**
	 * @callback ProgramCallback
	 * @param {TargetInstitutionProgramInfo} program
	 */

	class ModelerPathwaySearch {
		/**
		 * @param {Nullback} options.afterPickProgram
		 * @param {GetProgramNameUrl} options.getProgramNameUrl
		 * @param {string} options.targetProgramCoupleDelimiter
		 * @param {ProgramCallback} options.afterPickProgramFailure
		 */
		constructor(options) {
			this.afterPickProgram = options.afterPickProgram || (() => { /* noop */ });

			this.getProgramNameUrl = options.getProgramNameUrl;

			this.selectedPrograms = ko.observableArray([]);
					
			const coupleDelimiter = options.targetProgramCoupleDelimiter;

			const selectedProgramAsCouple = (prog) => `${prog.institutionProgramId}${coupleDelimiter}${prog.titleDisplay}`;

			this.targetProgramList = ko.computed(() =>
				this.selectedPrograms().map(selectedProgramAsCouple).join(coupleDelimiter));

			this.alreadyPickedProgramForBriefDisplay = ko.observable(null);
		}

		/**
		 * @param {PreSelectedProgram[]} [selectedPrograms=[]]
		 */
		init(selectedPrograms) {
			this.selectedPrograms(selectedPrograms || []);

			this.resolveProgramNames();
		}

		findProgramIndex(programId) {
			return this.selectedPrograms().findIndex((it) =>
				it.institutionProgramId === programId);
		}

		replaceMatchingProgram(program) {
			const i = this.findProgramIndex(program.institutionProgramId);

			if (i === -1) {
				return;
			}

			this.selectedPrograms.splice(i, 1, program);
		}

		pickProgram(program) {
			this.afterPickProgram();

			const id = program.institutionProgramId;

			if (this.findProgramIndex(id) !== -1) {
				this.alreadyPickedProgramForBriefDisplay(program);

				return;
			}

			this.alreadyPickedProgramForBriefDisplay(null);
			this.selectedPrograms.push(program);
			this.resolveProgramName(program);
		}

		removeProgram(programId) {
			const i = this.findProgramIndex(programId);

			if (i === -1) {
				return;
			}

			this.selectedPrograms.splice(i, 1);
		}

		resolveProgramNames() {
			const programs = this.selectedPrograms();

			programs.forEach((prog) => {
				if (!prog.isResolved) {
					this.resolveProgramName(prog);
				}
			});
		}

		resolveProgramName(program) {
			const url = this.getProgramNameUrl(program.institutionProgramId);

			App.common.getJson(url)
				.then((response) => {
					const updated = Object.assign({
						isResolved: true
					}, program, response.data);

					this.replaceMatchingProgram(updated);
				})
				.fail(() => {
					const updated = Object.assign({
						isResolved: true
					}, program);

					this.replaceMatchingProgram(updated);
				});
		}
	}
	
	class DisplayPathway {
		/**
		 * @param {ProgramPathwayListItem} pathwayData
		 * @param {PathwayProgramModel} program
		 * @param {PathwayIdCallback} makePathwayDetailUrl
		 */
		constructor(pathwayData, program, makePathwayDetailUrl) {
			Object.assign(this, pathwayData);

			this.program = program;
			this.detailUrl = makePathwayDetailUrl(this.pathwayId);
			this.displayTemplate = "pathway-template";
			this.creditRange = (() => {
				const min = pathwayData.minPotentialCredits || 0;
				const max = pathwayData.maxPotentialCredits || 0;
				const hasDistinctMinAndMax = (min !== max);

				if (hasDistinctMinAndMax) {
					return `${min.toFixed(2)} - ${max.toFixed(2)}`;
				}

				return (max || min || 0).toFixed(2);
			})();
		}
	}

	class DisplayComparable {
		constructor(comparable, program, makeDetailUrl) {
			Object.assign(this, comparable);

			this.program = program;
			this.detailUrl = makeDetailUrl(this.comparableId);
			this.displayTemplate = "comparable-template";

			this.creditRange = (() => {
				const min = comparable.minPotentialCredits || 0;
				const max = comparable.maxPotentialCredits || 0;
				const hasDistinctMinAndMax = (min !== max);

				if (hasDistinctMinAndMax) {
					return `${min.toFixed(2)} - ${max.toFixed(2)}`;
				}

				return (max || min || 0).toFixed(2);
			})();
		}
	}

	class ModelerProgramPathwayList {
		/**
		 * @callback GetUrlForProgram
		 * @param {int} programId
		 * @returns {string}
		 */

		/**
		 * @callback ProgramIdCallback
		 * @param {int} programId
		 */

		/**
		 * @callback PathwayIdCallback
		 * @param {int} pathwayId
		 */

		/**
		 * @callback ComparableIdCallback
		 * @param {int} comparableId
		 */

		/**
		 * @param {PathwayProgramModel} program
		 * @param {GetUrlForProgram} options.getUrlForProgramPathwayList
		 * @param {PathwayIdCallback} options.makePathwayDetailUrl
		 * @param {string} options.commonTerms[credits-low-to-high]
		 * @param {string} options.commonTerms[credits-high-to-low]
		 */
		constructor(program, options) {
			const commonTerms = options.commonTerms;

			this._options = options;
			this.program = program;
			this.fetchUrl = options.getUrlForProgramPathwayList(program.programId);

			this.pathways = ko.observableArray([]);
			this.comparables = ko.observableArray([]);

			this.sortType = ko.observable(sortCreditsHighToLow);
			this.sortType.subscribe((prev, cur) => {
				if (prev !== cur && !this.isExpanded()) {
					this.expand();
				}
			});
			this.sortTypeDisplay = ko.pureComputed(() => commonTerms[this.sortType()]);

			this.changeSortTypeTo = (val) => this.sortType(val);

			this.pathwayLoad = new App.Types.ObservableRemoteResource();

			this.isExpanded = ko.observable(false);
			
			this.programId = ko.computed(() => this.program.programId);

			this.sortedPathways = ko.computed(() => {
				if (!this.pathways()) {
					return [];
				}

				const sortFn = PathwaySorting[this.sortType()]
					|| PathwaySorting[sortCreditsHighToLow];

				return this.pathways().sort(sortFn);
			});

			this.allTransferable = ko.pureComputed(() => Array.prototype.concat([], this.pathways() || [], this.comparables() || []));

			this.sortedEntities = ko.pureComputed(() => {
				if (!this.allTransferable().length) {
					return [];
				}

				const sortFn = PathwaySorting[this.sortType()]
					|| PathwaySorting[sortCreditsHighToLow];

				return this.allTransferable().sort(sortFn);
			});
		}

		load() {
			if (this.pathwayLoad.inProgress()) {
				return;
			}

			this.pathwayLoad.start();
		
			const setPathways = () => {
				const pathways = this.pathwayLoad.data().pathways || [];
				const comparables = this.pathwayLoad.data().comparables || [];

				this.pathways(pathways.map(p => new DisplayPathway(p, this.program, this._options.makePathwayDetailUrl)));
				this.comparables(comparables.map(p => new DisplayComparable(p, this.program, this._options.makeComparableDetailUrl)));
			};				

			return App.common.getJson(this.fetchUrl)
				.then(this.pathwayLoad.okThen(setPathways))
				.fail(this.pathwayLoad.err);
		}

		ensureLoaded() {
			if (this.pathwayLoad.inProgress()
				|| this.pathwayLoad.complete()) {
				return;
			}

			this.load();
		}

		toggle() {
			if (this.isExpanded()) {
				this.collapse();
			} else {
				this.expand();
			}
		}

		expand() {
			this.ensureLoaded();
			this.isExpanded(true);
		}

		collapse() {
			this.isExpanded(false);
		}
	}

	class ModelerPathwayResultList {
		/**
		 * @param {GetUrlForProgram} options.getUrlForProgramPathwayList
		 * @param {PathwayIdCallback} options.makePathwayDetailUrl
		 * @param {ComparableIdCallback} options.makeComparableDetailUrl
		 * @param {string} options.commonTerms[credits-low-to-high]
		 * @param {string} options.commonTerms[credits-high-to-low]
		 */
		constructor(options) {
			this._options = options;

			this.programPathwayLists = ko.observableArray([]);
		}

		init(programs) {
			const progs = programs.map(prog =>
				new ModelerProgramPathwayList(prog, this._options));

			this.programPathwayLists(progs);

			if (programs.length) {
				this.findProgram(programs[0].programId).expand();
			}
		}

		findProgram(programId) {
			if (!this.programPathwayLists().length) {
				return null;
			}

			return this.programPathwayLists()
				.find(p => p.programId() === programId);
		}
	}

	exports.Modeler = exports.Modeler || {};
	exports.Modeler.ModelerPathwaySearch = ModelerPathwaySearch;
	exports.Modeler.ModelerPathwayResultList = ModelerPathwayResultList;
	exports.Modeler.ModelerProgramPathwayList = ModelerProgramPathwayList;
})(App);